import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Trash2, ArrowLeft, ArrowRight, ArrowUp, ArrowDown } from 'lucide-react';
import { Position, useUpdateNodeInternals } from 'reactflow';
import { v4 as uuidv4 } from 'uuid'; // Need to install uuid: npm install uuid @types/uuid

// Temporary fix: declare missing function from reactflow types
declare function updateNodeInternals(nodeId: string): void;

import { HandleConfig, HandleType, HandlePosition } from '../../types'; // Import types
import useStore from '../../store/useStore';
import { saveState } from '../../store/persistence';

// Don't destructure at the top level - we need to get fresh state each time
// This ensures we always have the latest state when functions are called

// Tailwind color palette with actual hex values
const TAILWIND_COLORS = [
  { name: 'Red', value: '#ef4444', bgClass: 'bg-red-500' },
  { name: 'Orange', value: '#f97316', bgClass: 'bg-orange-500' },
  { name: 'Yellow', value: '#eab308', bgClass: 'bg-yellow-500' },
  { name: 'Green', value: '#22c55e', bgClass: 'bg-green-500' },
  { name: 'Emerald', value: '#10b981', bgClass: 'bg-emerald-500' },
  { name: 'Cyan', value: '#06b6d4', bgClass: 'bg-cyan-500' },
  { name: 'Blue', value: '#3b82f6', bgClass: 'bg-blue-500' },
  { name: 'Violet', value: '#8b5cf6', bgClass: 'bg-violet-500' },
  { name: 'Purple', value: '#a855f7', bgClass: 'bg-purple-500' },
  { name: 'Pink', value: '#ec4899', bgClass: 'bg-pink-500' },
];

const DEFAULT_INPUT_COLOR_VALUE = '#22c55e'; // Green 500
const DEFAULT_OUTPUT_COLOR_VALUE = '#3b82f6'; // Blue 500

interface HandleManagerProps {
  nodeId: string; // Needed for unique dropdown IDs potentially
  handles: HandleConfig[] | undefined;
  onChange: (newHandles: HandleConfig[]) => void;
  maxHandles?: number;
  portalRoot?: HTMLElement | null; // Add portal root prop
}

// Helper to get default position based on type
const getDefaultPosition = (type: HandleType): HandlePosition => {
  return type === 'target' ? Position.Left : Position.Right;
};

// Helper to get default color value based on type
const getDefaultColor = (type: HandleType): string => {
  return type === 'target' ? DEFAULT_INPUT_COLOR_VALUE : DEFAULT_OUTPUT_COLOR_VALUE;
};

// Helper to ensure handle positions are aligned to the pixel grid
// This helps with the issue where handles don't perfectly align
const alignHandlePositions = (handles: HandleConfig[]): HandleConfig[] => {
  return handles.map(handle => {
    // Create a new handle object to avoid mutating the original
    return {
      ...handle,
      // No changes needed for the handle position itself as it's an enum
      // The actual pixel alignment happens in the React Flow internals
    };
  });
};

export default function HandleManager({
  nodeId,
  handles: initialHandles,
  onChange,
  maxHandles = 6,
  portalRoot = null,
}: HandleManagerProps) {
  const updateNodeInternals = useUpdateNodeInternals();

  const [handles, setHandles] = useState<HandleConfig[]>(Array.isArray(initialHandles) ? initialHandles : []);
  const [openDropdowns, setOpenDropdowns] = useState<{ [key: string]: 'color' | null }>({}); // Only 'color' dropdowns
  // Track dropdown positions for portal rendering
  const [dropdownPositions, setDropdownPositions] = useState<{
    [key: string]: { top: number; left: number; width: number };
  }>({});

  // Sync state with prop changes
  useEffect(() => {
    // Ensure initialHandles is always an array, even if undefined/null in props
    setHandles(Array.isArray(initialHandles) ? initialHandles : []);
  }, [initialHandles]);


  const toggleDropdown = (handleId: string, dropdownType: 'color', event?: React.MouseEvent) => { // Only 'color' dropdownType
    // If using portal, calculate position
    if (portalRoot && event?.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      setDropdownPositions(prev => ({
        ...prev,
        [`${handleId}-${dropdownType}`]: {
          top: Math.round(rect.bottom + window.scrollY),
          left: Math.round(rect.left + window.scrollX),
          width: rect.width
        }
      }));
    }

    setOpenDropdowns(prev => ({
      ...prev,
      [handleId]: prev[handleId] === dropdownType ? null : dropdownType,
    }));
  };

  const closeDropdown = (handleId: string) => {
    setOpenDropdowns(prev => ({ ...prev, [handleId]: null }));
  };

  const updateHandle = (id: string, updates: Partial<HandleConfig>) => {
    // Create updated handles array with the changes
    const newHandles = handles.map(h =>
      h.id === id ? { ...h, ...updates } : h
    );
    
    // Apply alignment to ensure handles are positioned correctly
    const alignedHandles = alignHandlePositions(newHandles);
    
    // Update local state
    setHandles(alignedHandles);
    onChange(alignedHandles); // Propagate change up
    closeDropdown(id); // Close dropdown after selection

    // Update node data.handles in Zustand
    try {
      // Get fresh state to ensure we're working with the latest data
      const store = useStore.getState();
      store.updateNode(nodeId, { handles: alignedHandles });
      store.setNodes((prevNodes: any) =>
        prevNodes.map((n: any) =>
          n.id === nodeId ? { ...n, data: { ...n.data, handles: alignedHandles } } : n
        )
      );
      updateNodeInternals(nodeId);
    } catch (error) {
      console.error('[HandleManager] Failed to update node handles after update', error);
    }
  };

  const addHandle = () => {
    if (handles.length >= maxHandles) return; // Enforce max limit

    const newHandle: HandleConfig = {
      id: uuidv4(), // Generate unique ID
      type: 'target', // Default to input
      position: getDefaultPosition('target'),
      color: getDefaultColor('target'),
    };
    
    // Create new handles array with the added handle
    const newHandles = [...handles, newHandle];
    
    // Apply alignment to ensure handles are positioned correctly
    const alignedHandles = alignHandlePositions(newHandles);
    
    // Update local state
    setHandles(alignedHandles);
    onChange(alignedHandles); // Propagate change up

    // Update node data.handles in Zustand
    try {
      // Get fresh state to ensure we're working with the latest data
      const store = useStore.getState();
      store.updateNode(nodeId, { handles: alignedHandles });
      store.setNodes((prevNodes: any) =>
        prevNodes.map((n: any) =>
          n.id === nodeId ? { ...n, data: { ...n.data, handles: alignedHandles } } : n
        )
      );
      updateNodeInternals(nodeId);
    } catch (error) {
      console.error('[HandleManager] Failed to update node handles after add', error);
    }
  };

  const removeHandle = (id: string) => {
    
    // First, capture the current edges before any changes
    const initialStore = useStore.getState();
    const initialEdges = [...initialStore.edges]; // Create a copy to avoid reference issues
    
    // CRITICAL: First, filter out edges connected to this specific handle
    const edgesToRemove: any[] = [];
    const edgesToKeep = initialEdges.filter((e: any) => {
      const isSourceMatch = e.source === nodeId && e.sourceHandle === id;
      const isTargetMatch = e.target === nodeId && e.targetHandle === id;
      
      if (isSourceMatch || isTargetMatch) {
        edgesToRemove.push(e);
        return false; // Remove this edge
      }
      return true; // Keep this edge
    });
    
    // If we found edges to remove, update the edges in the store
    if (edgesToKeep.length !== initialEdges.length) {

      
      initialStore.setEdges(edgesToKeep);
      

      
      // Immediately persist updated edges to localStorage
      try {
        const { nodes, viewport } = initialStore;
        saveState({ nodes, edges: edgesToKeep, viewport });
      } catch (error) {
        console.error('[HandleManager] Failed to persist state after edge removal', error);
      }
    } else {
    }
    
    // Update local state
    const newHandles = handles.filter(h => h.id !== id);
    
    // Apply alignment to ensure handles are positioned correctly
    const alignedHandles = alignHandlePositions(newHandles);
    
    setHandles(alignedHandles);
    onChange(alignedHandles); // Propagate change up

    // Update node data.handles in Zustand
    try {
      // Get fresh state to ensure we're working with the latest data
      const store = useStore.getState();
      
      // IMPORTANT: Store the node type before updating it


      
      // Update the node with new handles

      
      store.updateNode(nodeId, { handles: alignedHandles });
      store.setNodes((prevNodes: any) =>
        prevNodes.map((n: any) =>
          n.id === nodeId ? { ...n, data: { ...n.data, handles: alignedHandles } } : n
        )
      );
      updateNodeInternals(nodeId);
      

      
      // Double-check that our edges are still intact after node update
      const afterUpdateStore = useStore.getState();
      const afterUpdateEdges = afterUpdateStore.edges;
      
      // If edges were lost during node update, restore them
      if (afterUpdateEdges.length !== edgesToKeep.length) {
        afterUpdateStore.setEdges(edgesToKeep);
        
        // Persist again
        try {
          const { nodes, viewport } = afterUpdateStore;
          saveState({ nodes, edges: edgesToKeep, viewport });
        } catch (error) {
          console.error('[HandleManager] Failed to restore edges after node update', error);
        }
      }
    } catch (error) {
      console.error('[HandleManager] Error in removeHandle:', error);
    }
    
    // Take a single snapshot at the very end of the operation
    useStore.getState().takeSnapshot();
  };

  const handleTypeChange = (id: string, newType: HandleType) => {
    const currentHandle = handles.find(h => h.id === id);
    if (!currentHandle) return;

    const newPosition = getDefaultPosition(newType);
    const newColor = getDefaultColor(newType);

    updateHandle(id, { type: newType, position: newPosition, color: newColor });
  };

  const getPositionIcon = (position: HandlePosition) => {
    switch (position) {
      case Position.Left: return <ArrowLeft size={14} />;
      case Position.Right: return <ArrowRight size={14} />;
      case Position.Top: return <ArrowUp size={14} />;
      case Position.Bottom: return <ArrowDown size={14} />;
      default: return null;
    }
  };

  // Refs for dropdown elements
  const dropdownRefs = useRef<{ [key: string]: React.RefObject<HTMLDivElement> }>({});
  const triggerRefs = useRef<{ [key: string]: React.RefObject<HTMLButtonElement> }>({});

  // Initialize refs for new handles
  useEffect(() => {
    handles.forEach(handle => {
      // Only create refs for color dropdowns
      const key = `${handle.id}-color`;
      if (!dropdownRefs.current[key]) {
        dropdownRefs.current[key] = React.createRef<HTMLDivElement>();
      }
      if (!triggerRefs.current[key]) {
        triggerRefs.current[key] = React.createRef<HTMLButtonElement>();
      }
    });
  }, [handles]);

  // Click outside handler for dropdowns (Simplified Logic for color dropdown only)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      let clickedOutsideColorDropdown = true;

      Object.keys(openDropdowns).forEach(handleId => {
        if (openDropdowns[handleId] === 'color') { // Only consider open color dropdowns
          const key = `${handleId}-color`; // Use handleId here
          const triggerRef = triggerRefs.current[key];
          const dropdownRef = dropdownRefs.current[key];

          if (
            (triggerRef?.current && triggerRef.current.contains(event.target as Node)) ||
            (dropdownRef?.current && dropdownRef.current.contains(event.target as Node))
          ) {
            clickedOutsideColorDropdown = false;
          }
        }
      });

      if (clickedOutsideColorDropdown) {
        setOpenDropdowns({}); // Close all (color) dropdowns
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdowns]); // Re-attach listener if openDropdowns state changes


  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
          Handles ({handles.length}/{maxHandles})
        </label>
        <button
          type="button"
          onClick={addHandle}
          disabled={handles.length >= maxHandles}
          className={`p-1 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed`}
          aria-label="Add Handle"
        >
          <Plus size={18} />
        </button>
      </div>

      {/* Removed "No handles defined." message */}
      <div className="space-y-2">
        {handles.map((handle) => {
          const store = useStore.getState();
          // An input handle is connected if it's a 'target' and an edge points to it.
          const isInputHandleConnected = handle.type === 'target' &&
            store.edges.some((edge: any) => edge.target === nodeId && edge.targetHandle === handle.id);
          // An output handle is connected if it's a 'source' and an edge originates from it.
          const isOutputHandleConnected = handle.type === 'source' &&
            store.edges.some((edge: any) => edge.source === nodeId && edge.sourceHandle === handle.id);

          return (
            <div key={handle.id} className="flex items-center space-x-2">
              {/* Combined Config Bar */}
              <div className="flex-grow flex items-center space-x-1">
                {/* Color Dropdown */}
                <div className="relative flex items-center">
                  <button
                    ref={triggerRefs.current[`${handle.id}-color`]}
                    id={`handle-${handle.id}-color-trigger`}
                    type="button"
                    onClick={(e) => toggleDropdown(handle.id, 'color', e)}
                    className={`w-5 h-5 mr-1 border-2 dark:border-gray-600 rounded-full shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 flex items-center justify-center`}
                    style={{ backgroundColor: handle.color }}
                    title={`Color: ${TAILWIND_COLORS.find(c => c.value === handle.color)?.name || handle.color}`}
                  >
                    {/* Color swatch */}
                  </button>
                  {openDropdowns[handle.id] === 'color' && (
                    portalRoot ? createPortal(
                      <div
                        ref={dropdownRefs.current[`${handle.id}-color`]}
                        id={`handle-${handle.id}-color-dropdown`}
                        className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg p-1 grid grid-cols-5 gap-1 max-h-48 overflow-y-auto pointer-events-auto" // Adjusted grid cols
                        style={{
                          top: `${dropdownPositions[`${handle.id}-color`]?.top || 0}px`,
                          left: `${dropdownPositions[`${handle.id}-color`]?.left || 0}px`,
                          minWidth: '100px', // Min width for color grid
                        }}
                      >
                        {TAILWIND_COLORS.map(color => (
                          <button
                            key={color.name}
                            onClick={() => updateHandle(handle.id, { color: color.value })} // Store the actual color value
                            className={`w-5 h-5 rounded border border-gray-300 dark:border-gray-700 hover:ring-2 hover:ring-offset-1 dark:hover:ring-offset-gray-800 hover:ring-primary-500 flex items-center justify-center`} // Adjusted size
                            style={{ backgroundColor: color.value }} // Use inline style for picker options
                            title={color.name}
                          />
                        ))}
                      </div>,
                      portalRoot
                    ) : (
                      <div
                        id={`handle-${handle.id}-color-dropdown`}
                        className="absolute z-10 mt-1 w-auto min-w-[100px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg p-1 grid grid-cols-5 gap-1 max-h-48 overflow-y-auto pointer-events-auto" // Adjusted grid cols
                      >
                        {TAILWIND_COLORS.map(color => (
                          <button
                            key={color.name}
                            onClick={() => updateHandle(handle.id, { color: color.value })} // Store the actual color value
                            className={`w-5 h-5 rounded border border-gray-300 dark:border-gray-700 hover:ring-2 hover:ring-offset-1 dark:hover:ring-offset-gray-800 hover:ring-primary-500 flex items-center justify-center`} // Adjusted size
                            style={{ backgroundColor: color.value }} // Use inline style for picker options
                            title={color.name}
                          />
                        ))}
                      </div>
                    )
                  )}
                </div>

                {/* Type Toggle Group */}
                <div className="flex flex-1 rounded shadow-sm">
                  <button
                    type="button"
                    onClick={() => handleTypeChange(handle.id, 'target')}
                    disabled={isInputHandleConnected || isOutputHandleConnected}
                    className={`flex-1 h-6 flex items-center justify-center text-xs font-medium rounded-l border border-gray-200 dark:border-gray-700 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500
                      ${handle.type === 'target'
                        ? 'bg-blue-500/20 dark:bg-gray-600 text-gray-900 dark:text-gray-50'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }
                      disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    Input
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTypeChange(handle.id, 'source')}
                    disabled={isInputHandleConnected || isOutputHandleConnected}
                    className={`flex-1 h-6 flex items-center justify-center text-xs font-medium rounded-r border-t border-b border-r border-gray-200 dark:border-gray-700 -ml-px focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500
                      ${handle.type === 'source'
                        ? 'bg-blue-500/20 dark:bg-gray-600 text-gray-900 dark:text-gray-50'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }
                      disabled:opacity-30 disabled:cursor-not-allowed`}
                  >
                    Output
                  </button>
                </div>

                {/* Position Toggle Group */}
                <div className="flex flex-1 items-center rounded-md shadow-sm">
                  {(Object.values(Position) as HandlePosition[]).map((pos, index, arr) => (
                    <button
                      key={pos}
                      type="button"
                      onClick={() => updateHandle(handle.id, { position: pos })}
                      title={pos.charAt(0).toUpperCase() + pos.slice(1)}
                      className={`flex-1 h-6 flex items-center justify-center border border-gray-200 dark:border-gray-700 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500
                        ${index === 0 ? 'rounded-l' : '-ml-px'}
                        ${index === arr.length - 1 ? 'rounded-r' : ''}
                        ${handle.position === pos
                          ? 'bg-blue-500/20 dark:bg-gray-600 text-gray-900 dark:text-gray-50'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }
                      `}
                    >
                      {getPositionIcon(pos)}
                    </button>
                  ))}
                </div>

              </div>

              {/* Remove Button */}
              <button
                type="button"
                onClick={() => removeHandle(handle.id)}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-900/50"
                aria-label="Remove Handle"
              >
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
